import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {downloadBlob, isDefined} from '../../../commons/commons';
import {Router} from '@angular/router';
import {ECAcceptedDocument, ECMember, ECMemberDocument, ECMemberPOD, ECMemberState} from 'smag-commons/cfg/hierarchy/ec';
import {ConfigurationService} from '../../../shared/configuration.service';
import {BehaviorSubject, forkJoin, Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {MemberAccount} from '../account';
import {SmagApiService} from '../../../api/smag-api.service';
import {UserDisableReason} from 'smag-commons/dto/api';
import {I18NService} from '../../../i18n/i18n.service';
import {I18NDatePipe} from '../../../i18n/i18n.pipe';
import {AuthService} from 'auth-commons';
import {PodResource} from '../ec-members.component';
import {SpinnerData} from '../../../shared/spinner/spinner.component';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {StatisticsErrorType} from 'smag-commons/dto/api/statistics-errors';
import {HierarchyEntity} from '../../../shared/hierarchy.service';
import {getLabeledProfile} from '../../../shared/energy-community';
import {ECJuridicalType} from 'smag-commons/cfg/hierarchy';
import {addressToString, getPodRole, isPasswordExpired, isPodActive, prettySize} from '../../../shared/ec-members';
import {ECMemberPODRelationType} from 'smag-commons/cfg/hierarchy/ec';

interface EcDoc {
    type: string;
    id: string;
}

interface ECAcceptedDocumentWithId extends ECAcceptedDocument {
    id: string;
}

@Component({
    selector: 'app-ec-member-details',
    templateUrl: './ec-member-details.component.html',
    styleUrls: ['./ec-member-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ECMemberDetailsComponent implements OnInit {
    private i18nDatePipe: I18NDatePipe;
    private ecDocs: EcDoc[] = [];

    podTypeMap: { pod: string, type: string }[] = [];

    @Input() member: ECMember;
    @Input() account: MemberAccount;
    @Input() resources: PodResource[];
    @Input() canUpdate: boolean;
    @Input() show2fa: boolean;
    @Input() podsErrorsMap: { [podId: string]: StatisticsErrorType } = {};
    @Input() network: HierarchyEntity;
    @Output() disableAccount: EventEmitter<void> = new EventEmitter<void>();
    @Output() reactivateAccount: EventEmitter<void> = new EventEmitter<void>();
    @Output() forcePwdChange: EventEmitter<void> = new EventEmitter<void>();
    @Output() deleteMemberUser: EventEmitter<void> = new EventEmitter<void>();
    @Output() reset2FA: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;

    readonly addressToString = addressToString;
    readonly isDefined = isDefined;
    readonly prettySize = prettySize;
    readonly getPodRole = getPodRole;
    readonly ECJuridicalType = ECJuridicalType;

    constructor(private readonly router: Router,
                private readonly configurationService: ConfigurationService,
                private readonly apiService: SmagApiService,
                private readonly i18n: I18NService,
                private readonly authService: AuthService,
                private readonly dialogService: DialogService,
                private readonly msgBoxService: MsgBoxService) {
        this.i18nDatePipe = new I18NDatePipe(this.i18n, this.authService);
    }

    ngOnInit() {
        forkJoin(this.resources
            .map(podResource => this.apiService.getECPod(this.network?.id, podResource.entity.id)
                .pipe(map(ecNamedPod => ({pod: podResource.pod, type: getLabeledProfile(ecNamedPod)})))))
            .subscribe(podTypeMap => this.podTypeMap = podTypeMap);

        this.getEcDocuments().subscribe(docs => this.ecDocs = docs);
    }

    get canDisable(): boolean {
        return this.canUpdate && !!this.account && this.account.enabled;
    }

    get canEnable(): boolean {
        return this.canUpdate && !!this.account && !this.account.enabled;
    }

    get canRenewPassword(): boolean {
        return this.canUpdate && !!this.account && !this.account.mustChangePassword;
    }

    get canReset2FA(): boolean {
        return this.canUpdate && !!this.account && this.account.enabled2fa;
    }

    get canBeInvited(): boolean {
        return (this.member.state === ECMemberState.NOT_INVITED
                || this.member.state === ECMemberState.PENDING
                || this.member.state === ECMemberState.CANCELED) &&
            isDefined(this.member.personal_data?.mobile_number) &&
            isDefined(this.member.personal_data?.email) &&
            isDefined(this.member.access_type);
    }

    memberIsOwnerOnly() {
        return this.member.pod_relations.every(relation => relation.type === ECMemberPODRelationType.OWNER);
    }

    getPodType(pod: string) {
        return this.podTypeMap.find(entry => entry.pod === pod)?.type;
    }

    mkAddress(): string {
        return addressToString(this.member.personal_data?.address);
    }

    mkDisableReasonMessage(reason: UserDisableReason) {
        const disabledData = this.i18nDatePipe.transform(this.account.disabledOn);
        switch (reason) {
            case UserDisableReason.DISABLED_BY_ADMIN:
                return this.i18n.translate('Manually disabled since {0}', disabledData);
            case UserDisableReason.INACTIVE:
                return this.i18n.translate('Automatically disabled since {0} due to inactivity', disabledData);
            case UserDisableReason.FAILED_LOGIN:
                return this.i18n.translate('Automatically disabled since {0} due to too many failed logins', disabledData);
            default:
                return this.i18n.translate('Disabled member since {0}', disabledData);
        }
    }

    hasFile(doc: ECMemberDocument): boolean {
        return !!doc.filename;
    }

    isPodActive(pod: ECMemberPOD): Observable<boolean> {
        return isPodActive(pod, this.configurationService);
    }

    isPasswordOk(account: MemberAccount): boolean {
        return !account.mustChangePassword && !isPasswordExpired(account);
    }

    onClickDisableAccount() {
        this.disableAccount.emit();
    }

    onClickReactivateAccount() {
        this.reactivateAccount.emit();
    }

    onClickForcePwdChange() {
        this.forcePwdChange.emit();
    }

    onClickDeleteMemberUser() {
        this.deleteMemberUser.emit();
    }

    onClickReset2FA() {
        this.reset2FA.emit();
    }

    onClickDownloadDocument(doc: ECMemberDocument) {
        this.apiService.getMemberDocument(this.member.id, doc.id).subscribe(([data, filename]) => downloadBlob(data, filename));
    }

    onClickDownloadAcceptedDocument(docId: string) {
        if (docId) {
            this.apiService.getECDocument(this.network.id, docId).subscribe(([data, filename]) => downloadBlob(data, filename));
        }
    }

    hasResource(podCode: string) {
        if (this.resources.length !== 0) {
            const podResource = this.resources.find((pr) => pr.pod === podCode);
            if (podResource) {
                return podResource.entity.name;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    navigateToEntity(podCode: string) {
        const podResource = this.resources.find((pr) => pr.pod === podCode);
        if (podResource) {
            // navigate to selected entity (within configuration page)
            this.router.navigate(['/c', 'ec', 'resources'], {
                queryParams: {
                    id: podResource.entity.id
                },
                queryParamsHandling: 'merge'
            }).then();
        }
    }

    getAcceptedDocuments(member: ECMember): ECAcceptedDocumentWithId[] {
        const memberDocs = (member.accepted_documents ?? [])
            .map(acceptedDoc => ({
                ...acceptedDoc,
                id: this.ecDocs.find(doc => doc.type === acceptedDoc.name)?.id
            } as ECAcceptedDocumentWithId));
        const notAcceptedDocs = this.ecDocs
            .filter(doc => !memberDocs.map(md => md.name).includes(doc.type))
            .map(doc => ({name: doc.type, accepted: false, timestamp: 0, id: doc.id} as ECAcceptedDocumentWithId));
        return [...memberDocs, ...notAcceptedDocs];
    }

    async onClickSendInvitation(showSpinner: boolean) {
        try {
            const spinner = showSpinner ? this.openSpinner('We are sending the invitation') : null;
            this.apiService.inviteMember(this.member.id)
                .then(() => {
                    if (!showSpinner) {
                        this.msgBoxService.info(this.i18n.translate('Invitation sent successfully'));
                    } else {
                        spinner.context.next({
                            loading: false,
                            loadingMessage: this.i18n.translate('Invitation sent successfully')
                        });
                    }
                    // Locally set to pending member state to avoid change access type after sent first invitation
                    this.member.state = ECMemberState.PENDING;
                })
                .catch(err => {
                    if (showSpinner) {
                        spinner.context.next({
                            loading: false,
                            loadingMessage: this.i18n.translate('Could not send invitation to subject'),
                            abort: true
                        });
                    }
                    this.msgBoxService.error(this.i18n.translate('Could not send invitation to subject'), err);
                });
        } catch (err) {
            console.error('Cannot invite subject', err);
        }
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: 700,
            hideCloseButton: true
        });
    }

    private getEcDocuments(): Observable<EcDoc[]> {
        return this.apiService.listECDocuments(1, 9999, this.network.id, null, true, null)
            .pipe(map(result => result.results.map(doc => ({type: doc.document_type, id: doc.id} as EcDoc))));
    }
}
